/* eslint-disable sort-keys */
import Auth from '../managers/AuthManager';
import basicIframeModalLauncherManager from '../managers/BasicIframeModalLauncherManager';
import contentManager from '../managers/ContentManager';
import userManager from '../managers/UserManager';

import { registerClass } from '../SatCoreRegistry';
import { generateRobustUrl } from '../utils';

export default class BasicIframeModalLauncherService {
  static allowGettingStartedIframeModalLauncher = (iframeModalLauncherObj) => {
    const obj = iframeModalLauncherObj || this.getFloatingHelpIframeLauncherMapEntryValue();
    const isMicrosite = obj?.resourceType === 'microsite_resource';
    return obj?.url || (isMicrosite && obj?.resourceId);
  }

  /**
   * Build a complete caution banner message config object from an iframe modal launcher
   * @param {Object} iframeModalLauncherObj - Iframe modal launcher configuration object
   * @returns {Object|null} Complete banner config object ready to be pushed to cautionBannerMsgConfigArray, or null if no banner config
   */
  static buildBasicIframeModalLauncherCautionBannerConfig = (iframeModalLauncherObj) => {
    if (!iframeModalLauncherObj?.bannerConfig) {
      return null;
    }

    const { bannerConfig } = iframeModalLauncherObj;

    return {
      _id: bannerConfig._id || iframeModalLauncherObj._id,
      actionConfig: {
        buttonLabelKey: bannerConfig.actionConfig?.buttonLabelKey || '',
        headerTitleKey: bannerConfig.actionConfig?.headerTitleKey || '',
        iframeModalLauncherObj
      },
      actionType: bannerConfig.actionType || 'IFRAME_MODAL_LAUNCHER',
      bannerMsgType: bannerConfig.bannerMsgType || 'MESSAGE_WITH_ACTION',
      translationKey: bannerConfig.translationKey || '',
      pathnames: bannerConfig.pathnames || []
    };
  }

  /**
   * Check if the given iframe modal launcher has a caution banner configured for the current pathname
   * @param {string} pathname - Current pathname (e.g., '/content')
   * @param {Object} iframeModalLauncherObj - Iframe modal launcher configuration object
   * @returns {boolean} Whether the iframe modal launcher has a caution banner configured for this pathname
   */
  static allowBasicIframeModalLauncherCautionBanner = (pathname, iframeModalLauncherObj) => {
    const bannerConfig = this.buildBasicIframeModalLauncherCautionBannerConfig(iframeModalLauncherObj);

    if (!bannerConfig) {
      return false;
    }

    const { pathnames } = bannerConfig;

    if (!Array.isArray(pathnames) || pathnames.length === 0) {
      return false;
    }

    return pathnames.includes(pathname);
  }

  static getDashboardGettingStartedIframeLauncherMapEntryValue = () => {
    if (userManager.isDistrictOrSchoolAdmin) {
      return basicIframeModalLauncherManager.basicIframeModalLauncherUrlMap?.get(
        'DASHBOARD_GETTING_STARTED_ADMIN'
      );
    } else if (userManager.isTeacher) {
      return basicIframeModalLauncherManager.basicIframeModalLauncherUrlMap?.get(
        'DASHBOARD_GETTING_STARTED_TEACHER'
      );
    } else if (userManager.isStudent) {
      return basicIframeModalLauncherManager.basicIframeModalLauncherUrlMap?.get(
        'DASHBOARD_GETTING_STARTED_STUDENT'
      );
    }
    return null;
  }

  static allowFloatingHelpIframeModalLauncher = (iframeModalLauncherObj) => {
    const obj = iframeModalLauncherObj || this.getFloatingHelpIframeLauncherMapEntryValue();
    const isMicrosite = obj?.resourceType === 'microsite_resource';
    return obj?.url || (isMicrosite && obj?.resourceId);
  }

  static getFloatingHelpIframeLauncherMapEntryValue = () => {
    if (userManager.isDistrictOrSchoolAdmin) {
      return basicIframeModalLauncherManager.basicIframeModalLauncherUrlMap?.get(
        'FLOATING_HELP_ADMIN'
      );
    } else if (userManager.isTeacher) {
      return basicIframeModalLauncherManager.basicIframeModalLauncherUrlMap?.get(
        'FLOATING_HELP_TEACHER'
      );
    } else if (userManager.isStudent) {
      return basicIframeModalLauncherManager.basicIframeModalLauncherUrlMap?.get(
        'FLOATING_HELP_STUDENT'
      );
    }
    return null;
  }

  static getDashboardGettingStartedIframeModalLauncherConfig = async (iframeModalLauncherObj) => {
    iframeModalLauncherObj = iframeModalLauncherObj || this.getDashboardGettingStartedIframeLauncherMapEntryValue();
    return this.generateIframeModalLauncherConfig(iframeModalLauncherObj);
  }

  static getFloatingHelpIframeModalLauncherConfig = async (iframeModalLauncherObj) => {
    iframeModalLauncherObj = iframeModalLauncherObj || this.getFloatingHelpIframeLauncherMapEntryValue();
    return this.generateIframeModalLauncherConfig(iframeModalLauncherObj);
  }

  static generateIframeModalLauncherConfig = async (iframeModalLauncherObj) => {
    if (!iframeModalLauncherObj) {
      return null;
    }

    const obj = iframeModalLauncherObj;
    let baseUrl;

    const isMicrosite = obj?.resourceType === 'microsite_resource';
    if (isMicrosite && obj.resourceId/* && obj.contentItemId */) {
      baseUrl = await contentManager.fetchMicrositeRedirectUrl({
        resourceId: obj.resourceId
      });
    } else {
      baseUrl = iframeModalLauncherObj.url;
    }

    if (!baseUrl) {
      return null;
    }

    // Build query params from customParamKeys
    const customParamObj = this.getIframeLauncherUrlCustomParamObj(obj.customParamKeys);
    const urlWithParams = generateRobustUrl(baseUrl, customParamObj);

    // Build postMessage params separately if configured
    const postMessageParamObj = this.getPostMessageParamObj(obj.postMessageParamKeys);

    // Always return an object for consistency
    return {
      postMessageParamObj,
      postMessageTargetOrigin: obj.postMessageTargetOrigin,
      url: urlWithParams
    };
  }

  static getIframeLauncherUrlCustomParamObj = (customParamKeys) => {
    if (Array.isArray(customParamKeys) && customParamKeys.length > 0) {
      const customParamObj = {
        ...(customParamKeys.includes('authKey') ? { authKey: Auth.authKey } : {}),
        ...(customParamKeys.includes('ecmsBaseUrl') ? { ecmsBaseUrl: Auth.ecms } : {}),
        ...(customParamKeys.includes('userId') ? { userId: userManager.userId } : {})
      };
      return customParamObj;
    }
    return {};
  }

  /**
   * Build parameter object for postMessage delivery to iframe
   * @param {Array} postMessageParamKeys - Array of parameter keys to include (e.g., ['authKey', 'userId'])
   * @returns {Object} Object containing the requested parameters
   */
  static getPostMessageParamObj = (postMessageParamKeys) => {
    if (Array.isArray(postMessageParamKeys) && postMessageParamKeys.length > 0) {
      const postMessageParamObj = {
        ...(postMessageParamKeys.includes('authKey') ? { authKey: Auth.authKey } : {}),
        ...(postMessageParamKeys.includes('ecmsBaseUrl') ? { ecmsBaseUrl: Auth.ecms } : {}),
        ...(postMessageParamKeys.includes('userId') ? { userId: userManager.userId } : {})
      };
      return postMessageParamObj;
    }
    return {};
  }
}

registerClass('BasicIframeModalLauncherService', BasicIframeModalLauncherService);
